import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faKey, faToggleOn, faToggleOff, faCoins, faLink,
  faCopy, faEye, faEyeSlash, faCheck, faRefresh, faPlus, faTimes
} from '@fortawesome/free-solid-svg-icons';
import {
  getClientes, getCliente, generarApiKeys, actualizarWebhook,
  actualizarCreditos, actualizarApiEnabled, getConsultasCliente, crearCliente
} from '../../services/api';
import './Clientes.css';

const Clientes = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedCliente, setSelectedCliente] = useState(null);
  const [clienteDetail, setClienteDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const [copied, setCopied] = useState(null);

  const [webhookInput, setWebhookInput] = useState('');
  const [creditosInput, setCreditosInput] = useState(0);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [savingCreditos, setSavingCreditos] = useState(false);

  const [consultasCliente, setConsultasCliente] = useState([]);
  const [loadingConsultas, setLoadingConsultas] = useState(false);

  // Modal crear cliente
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingCliente, setCreatingCliente] = useState(false);
  const [newCliente, setNewCliente] = useState({
    username: '',
    password: '',
    nombre_completo: '',
    creditos: 0,
    api_enabled: false,
    webhook_url: ''
  });

  useEffect(() => {
    fetchClientes();
  }, [page]);

  const fetchClientes = async () => {
    setLoading(true);
    try {
      const data = await getClientes({ page, page_size: 20 });
      setClientes(data.items);
      setTotalPages(data.total_pages);
    } catch (err) {
      setError('Error al cargar clientes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCliente = async (cliente) => {
    setSelectedCliente(cliente);
    setNewSecret(null);
    setShowSecret(false);
    setLoadingDetail(true);

    try {
      const detail = await getCliente(cliente.id);
      setClienteDetail(detail);
      setWebhookInput(detail.webhook_url || '');
      setCreditosInput(detail.creditos);

      // Cargar consultas del cliente
      setLoadingConsultas(true);
      const consultas = await getConsultasCliente(cliente.id, { page: 1, page_size: 10 });
      setConsultasCliente(consultas.items);
    } catch (err) {
      console.error('Error cargando cliente:', err);
    } finally {
      setLoadingDetail(false);
      setLoadingConsultas(false);
    }
  };

  const handleGenerarApiKeys = async () => {
    if (!selectedCliente) return;

    if (!window.confirm('Esto regenerara las API keys. Las keys anteriores dejaran de funcionar. Continuar?')) {
      return;
    }

    try {
      const result = await generarApiKeys(selectedCliente.id);
      setNewSecret(result.api_secret);
      setClienteDetail(prev => ({ ...prev, api_client_id: result.api_client_id }));
      fetchClientes();
    } catch (err) {
      alert('Error al generar API keys');
      console.error(err);
    }
  };

  const handleToggleApiEnabled = async () => {
    if (!selectedCliente || !clienteDetail) return;

    try {
      await actualizarApiEnabled(selectedCliente.id, !clienteDetail.api_enabled);
      setClienteDetail(prev => ({ ...prev, api_enabled: !prev.api_enabled }));
      fetchClientes();
    } catch (err) {
      alert('Error al actualizar estado API');
      console.error(err);
    }
  };

  const handleSaveWebhook = async () => {
    if (!selectedCliente) return;
    setSavingWebhook(true);

    try {
      await actualizarWebhook(selectedCliente.id, webhookInput);
      setClienteDetail(prev => ({ ...prev, webhook_url: webhookInput }));
    } catch (err) {
      alert('Error al guardar webhook');
      console.error(err);
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleSaveCreditos = async () => {
    if (!selectedCliente) return;
    setSavingCreditos(true);

    try {
      await actualizarCreditos(selectedCliente.id, parseInt(creditosInput));
      setClienteDetail(prev => ({ ...prev, creditos: parseInt(creditosInput) }));
      fetchClientes();
    } catch (err) {
      alert('Error al guardar creditos');
      console.error(err);
    } finally {
      setSavingCreditos(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-AR');
  };

  const handleCreateCliente = async (e) => {
    e.preventDefault();
    if (!newCliente.username || !newCliente.password) {
      alert('Username y password son requeridos');
      return;
    }

    setCreatingCliente(true);
    try {
      await crearCliente(newCliente);
      setShowCreateModal(false);
      setNewCliente({
        username: '',
        password: '',
        nombre_completo: '',
        creditos: 0,
        api_enabled: false,
        webhook_url: ''
      });
      fetchClientes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear cliente');
      console.error(err);
    } finally {
      setCreatingCliente(false);
    }
  };

  return (
    <div className="clientes-page">
      <div className="clientes-header">
        <h1 className="clientes-title">Gestion de Clientes API</h1>
        <button className="btn-create" onClick={() => setShowCreateModal(true)}>
          <FontAwesomeIcon icon={faPlus} /> Nuevo Cliente
        </button>
      </div>

      {/* Modal Crear Cliente */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Crear Nuevo Cliente</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={handleCreateCliente}>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={newCliente.username}
                  onChange={(e) => setNewCliente({...newCliente, username: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={newCliente.password}
                  onChange={(e) => setNewCliente({...newCliente, password: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nombre Completo</label>
                <input
                  type="text"
                  value={newCliente.nombre_completo}
                  onChange={(e) => setNewCliente({...newCliente, nombre_completo: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Creditos Iniciales</label>
                <input
                  type="number"
                  value={newCliente.creditos}
                  onChange={(e) => setNewCliente({...newCliente, creditos: parseInt(e.target.value) || 0})}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Webhook URL</label>
                <input
                  type="url"
                  value={newCliente.webhook_url}
                  onChange={(e) => setNewCliente({...newCliente, webhook_url: e.target.value})}
                  placeholder="https://..."
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newCliente.api_enabled}
                    onChange={(e) => setNewCliente({...newCliente, api_enabled: e.target.checked})}
                  />
                  Habilitar API
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowCreateModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-submit" disabled={creatingCliente}>
                  {creatingCliente ? 'Creando...' : 'Crear Cliente'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="clientes-layout">
        <div className="clientes-list-container">
          {loading ? (
            <div className="clientes-loading">Cargando clientes...</div>
          ) : error ? (
            <div className="clientes-error">{error}</div>
          ) : (
            <>
              <table className="clientes-table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>API</th>
                    <th>Creditos</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {clientes.map(cliente => (
                    <tr
                      key={cliente.id}
                      className={selectedCliente?.id === cliente.id ? 'selected' : ''}
                      onClick={() => handleSelectCliente(cliente)}
                    >
                      <td className="td-username">
                        <div className="username-cell">
                          <span className="username">{cliente.username}</span>
                          {cliente.nombre_completo && (
                            <span className="nombre">{cliente.nombre_completo}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`api-badge ${cliente.api_enabled ? 'enabled' : 'disabled'}`}>
                          {cliente.api_enabled ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="td-creditos">
                        <span className={`creditos ${cliente.creditos === 0 ? 'zero' : ''}`}>
                          {cliente.creditos}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${cliente.is_active ? 'active' : 'inactive'}`}>
                          {cliente.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="pagination">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                    Anterior
                  </button>
                  <span className="page-info">Pagina {page} de {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    Siguiente
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="cliente-detail-panel">
          {selectedCliente && clienteDetail ? (
            loadingDetail ? (
              <div className="detail-loading">Cargando...</div>
            ) : (
              <>
                <div className="detail-header">
                  <h2>{clienteDetail.username}</h2>
                  {clienteDetail.nombre_completo && <p>{clienteDetail.nombre_completo}</p>}
                </div>

                <div className="detail-section">
                  <h3><FontAwesomeIcon icon={faKey} /> API Keys</h3>

                  <div className="api-keys-section">
                    <div className="key-row">
                      <label>Client ID:</label>
                      <div className="key-value">
                        <code>{clienteDetail.api_client_id || 'No generado'}</code>
                        {clienteDetail.api_client_id && (
                          <button
                            className="copy-btn"
                            onClick={() => copyToClipboard(clienteDetail.api_client_id, 'client_id')}
                          >
                            <FontAwesomeIcon icon={copied === 'client_id' ? faCheck : faCopy} />
                          </button>
                        )}
                      </div>
                    </div>

                    {newSecret && (
                      <div className="key-row secret-row">
                        <label>API Secret (guardar ahora):</label>
                        <div className="key-value">
                          <code className={showSecret ? '' : 'hidden'}>
                            {showSecret ? newSecret : '********************************'}
                          </code>
                          <button className="copy-btn" onClick={() => setShowSecret(!showSecret)}>
                            <FontAwesomeIcon icon={showSecret ? faEyeSlash : faEye} />
                          </button>
                          <button
                            className="copy-btn"
                            onClick={() => copyToClipboard(newSecret, 'secret')}
                          >
                            <FontAwesomeIcon icon={copied === 'secret' ? faCheck : faCopy} />
                          </button>
                        </div>
                        <p className="secret-warning">Este secret no se puede recuperar. Guardelo ahora.</p>
                      </div>
                    )}

                    <button className="btn-generate" onClick={handleGenerarApiKeys}>
                      <FontAwesomeIcon icon={faRefresh} />
                      {clienteDetail.api_client_id ? 'Regenerar Keys' : 'Generar Keys'}
                    </button>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>
                    <FontAwesomeIcon icon={clienteDetail.api_enabled ? faToggleOn : faToggleOff} />
                    Acceso API
                  </h3>
                  <div className="toggle-section">
                    <button
                      className={`btn-toggle ${clienteDetail.api_enabled ? 'enabled' : ''}`}
                      onClick={handleToggleApiEnabled}
                    >
                      {clienteDetail.api_enabled ? 'Deshabilitar API' : 'Habilitar API'}
                    </button>
                  </div>
                </div>

                <div className="detail-section">
                  <h3><FontAwesomeIcon icon={faLink} /> Webhook URL</h3>
                  <div className="input-group">
                    <input
                      type="url"
                      value={webhookInput}
                      onChange={(e) => setWebhookInput(e.target.value)}
                      placeholder="https://ejemplo.com/webhook"
                    />
                    <button
                      className="btn-save"
                      onClick={handleSaveWebhook}
                      disabled={savingWebhook}
                    >
                      {savingWebhook ? 'Guardando...' : 'Guardar'}
                    </button>
                  </div>
                </div>

                <div className="detail-section">
                  <h3><FontAwesomeIcon icon={faCoins} /> Creditos</h3>
                  <div className="input-group">
                    <input
                      type="number"
                      value={creditosInput}
                      onChange={(e) => setCreditosInput(e.target.value)}
                      min="0"
                    />
                    <button
                      className="btn-save"
                      onClick={handleSaveCreditos}
                      disabled={savingCreditos}
                    >
                      {savingCreditos ? 'Guardando...' : 'Guardar'}
                    </button>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Ultimas Consultas API</h3>
                  {loadingConsultas ? (
                    <div className="loading-consultas">Cargando...</div>
                  ) : consultasCliente.length > 0 ? (
                    <table className="consultas-mini-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Patente</th>
                          <th>Estado</th>
                          <th>Webhook</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consultasCliente.map(c => (
                          <tr key={c.id}>
                            <td>{formatDate(c.created_at)}</td>
                            <td>{c.patente}</td>
                            <td>
                              <span className={`estado-badge ${c.estado.toLowerCase()}`}>
                                {c.estado}
                              </span>
                            </td>
                            <td>
                              <span className={`webhook-status ${c.webhook_enviado ? 'sent' : 'pending'}`}>
                                {c.webhook_enviado ? 'Enviado' : `Pendiente (${c.webhook_intentos})`}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="no-consultas">Sin consultas API registradas</p>
                  )}
                </div>
              </>
            )
          ) : (
            <div className="no-selection">
              <p>Seleccione un cliente para ver sus detalles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Clientes;
